import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import authService from "../services/authService";
import { logger } from "../utils/logger";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [superAdminExists, setSuperAdminExists] = useState(null);

  const [accessToken, setAccessToken] = useState(null);

  const initialize = useCallback(async () => {
    try {
      setError(null);
      logger.info("Initializing BO app...");

      const saCheck = await authService.checkSuperAdmin();
      const exists = saCheck.exists === true;
      setSuperAdminExists(exists);
      logger.info("Super admin check result", { exists });

      if (!exists) {
        setUser(null);
        setAccessToken(null);
        setLoading(false);
        return;
      }

      const data = await authService.checkAuth();
      const userData = data?.user || data;

      if (userData) {
        setUser(userData);
        setAccessToken(data?.accessToken || null);
        logger.info("BO user authenticated:", {
          username: userData.username,
          email: userData.email,
          role: userData.role,
        });
      } else {
        setUser(null);
        setAccessToken(null);
        logger.info("No authenticated BO user found");
      }
    } catch (err) {
      logger.error("Initialization error:", err.message);
      setUser(null);
      setAccessToken(null);
      setSuperAdminExists((prev) => (prev === null ? true : prev));
    } finally {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    try {
      setError(null);
      logger.info("Attempting BO login for:", { email });

      const data = await authService.login(email, password);
      const userData = data?.user || data;

      if (userData) {
        setUser(userData);
        setAccessToken(data?.accessToken || null);
        logger.info("BO login successful:", {
          username: userData.username,
          role: userData.role,
        });
        return {
          success: true,
          user: userData,
        };
      } else {
        throw new Error("Login failed: Profile incomplete");
      }
    } catch (err) {
      logger.error("Login error:", err.message);
      return { success: false, error: err.message };
    }
  };

  const signup = async ({ email, password, username, firstName, lastName }) => {
    try {
      setError(null);
      logger.info("Attempting super admin signup for:", { email });

      const data = await authService.signup({
        email,
        password,
        username,
        firstName,
        lastName,
      });

      const userData = data?.user || data;

      if (userData) {
        setUser(userData);
        setAccessToken(data?.accessToken || null);
        setSuperAdminExists(true);
        logger.info("Super admin signup and auto-login successful:", {
          username: userData.username,
          role: userData.role,
        });
        return {
          success: true,
          user: userData,
        };
      } else {
        throw new Error("Sign Up failed: Profile incomplete");
      }
    } catch (err) {
      logger.error("Sign Up error:", err.message);
      return { success: false, error: err.message };
    }
  };

  const logout = async (skipApiCall = false) => {
    try {
      logger.info("Logging out...");
      if (!skipApiCall) {
        await authService.logout();
        logger.info("Logout successful");
      }
    } catch (err) {
      logger.error("Logout error:", err.message);
    } finally {
      setUser(null);
      setAccessToken(null);
      setError(null);
      logger.info("User state cleared after logout");
    }
  };

  const clearError = () => setError(null);

  useEffect(() => {
    initialize();
  }, [initialize]);

  useEffect(() => {
    if (!user) return;

    const interval = setInterval(
      async () => {
        if (document.visibilityState === "hidden") return;
        try {
          const data = await authService.checkAuth();
          const userData = data?.user || data;
          if (!userData) {
            logger.warn("Periodic auth check failed, logging out");
            setUser(null);
            setAccessToken(null);
          } else {
            setAccessToken(data?.accessToken || null);
          }
        } catch (err) {
          logger.error("Periodic auth check error:", err.message);
          setUser(null);
          setAccessToken(null);
        }
      },
      5 * 60 * 1000,
    );

    return () => clearInterval(interval);
  }, [user]);

  const value = {
    user,
    accessToken,
    loading,
    error,
    superAdminExists,
    login,
    signup,
    logout,
    clearError,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
